"use client";

import { motion } from "framer-motion";
import { Search } from "lucide-react";
import React, { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import { getAllCategories, Post, posts } from "@/data/blog-posts";

import { CardPostList } from "./card-post-list";

export default function PostsList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [filteredPosts, setFilteredPosts] = useState<Post[]>(posts);
  const [categorySearch, setCategorySearch] = useState("");

  const categories = [...getAllCategories()];

  useEffect(() => {
    let filtered = [...posts];

    // 🔍 Filtro por texto (título ou resumo)
    if (searchTerm) {
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // 🏷️ Filtro por categoria
    if (activeCategory !== "Todos") {
      filtered = filtered.filter((post) => post.category === activeCategory);
    }

    setFilteredPosts(filtered);
  }, [searchTerm, activeCategory]);

  // 🔎 Filtra categorias mostradas no aside
  const filteredCategories = categories.filter((category) =>
    category.toLowerCase().includes(categorySearch.toLowerCase()),
  );

  return (
    <div className="section pt-10 pb-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[1fr_300px]">
          <div>
            <div className="grid grid-cols-1 gap-6">
              {filteredPosts.length > 0 ? (
                [...filteredPosts].reverse().map((post, index) => (
                  <motion.div
                    key={index}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 * index }}
                  >
                    <CardPostList post={post} />
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full py-12 text-center">
                  <p className="text-muted-foreground text-lg">
                    Nenhum artigo encontrado. Tente outra busca.
                  </p>
                </div>
              )}
            </div>
          </div>

          <aside className="rounded-lg border shadow-sm">
            <div className="sticky top-28">
              <div className="mb-6 border border-white/20 px-6 pt-6">
                <h3 className="text-gold mb-4 text-sm uppercase">Pesquisar</h3>
                <div className="relative">
                  <Search
                    size={16}
                    className="absolute top-1/2 left-2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    type="text"
                    placeholder="Buscar artigos..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="border border-white/20 px-6 pb-6">
                <h3 className="text-gold mb-4 text-sm uppercase">Categorias</h3>

                <div className="relative mb-4">
                  <Search
                    size={16}
                    className="absolute top-1/2 left-2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    type="text"
                    placeholder="Buscar categoria..."
                    className="pl-8"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                  />
                </div>

                <ul className="space-y-2 overflow-y-auto">
                  <li
                    className={`border-separate cursor-pointer border-b border-white/10 pb-2 text-sm transition-colors ${
                      activeCategory === "Todos"
                        ? "text-primary font-semibold"
                        : "text-gray-700"
                    }`}
                    onClick={() => setActiveCategory("Todos")}
                  >
                    Todos
                  </li>
                  {filteredCategories.map((category) => (
                    <li
                      key={category}
                      className={`border-separate cursor-pointer border-b border-white/10 pb-2 text-sm transition-colors ${
                        activeCategory === category
                          ? "text-primary font-semibold"
                          : "text-gray-700"
                      }`}
                      onClick={() => setActiveCategory(category)}
                    >
                      {category}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
